import React, { useState, useEffect } from 'react';
import { motion } from "framer-motion";
import { Check, X, Star, Users, Shield, DollarSign, Zap, BarChart3, Globe2, Award, ArrowRight, Play, TrendingUp, Clock, Target, Menu, ChevronRight, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import CompanyLayout from "@/components/CompanyLayout";
import { DemoBookingModal } from "@/components/DemoBookingModal";
import { SupademoModal } from "@/components/SupademoModal";

const AssemblyVsNectar = () => {
  const [activeTab, setActiveTab] = useState('recognition');
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [isSupademoModalOpen, setIsSupademoModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('overview');

  // Table of Contents items
  const tocItems = [
    { id: 'overview', label: 'Overview' },
    { id: 'features', label: 'Features' },
    { id: 'pricing', label: 'Pricing' },
    { id: 'ease-of-use', label: 'Ease of Use' },
    { id: 'reviews', label: 'Reviews' },
    { id: 'roi', label: 'ROI' },
    { id: 'comparison', label: 'Which Platform' },
    { id: 'conclusion', label: 'Conclusion' },
    { id: 'faqs', label: 'FAQs' }
  ];

  // Scroll tracking for active section
  useEffect(() => {
    const handleScroll = () => {
      const sections = tocItems.map(item => document.getElementById(item.id));
      const scrollPosition = window.scrollY + 100;

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(tocItems[i].id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setIsMobileMenuOpen(false);
  };

  const featureCategories = {
    features: "Core Features",
    analytics: "Analytics & Insights", 
    support: "Support & Security"
  };

  const comparisonData = {
    features: [
      { feature: "AI-Powered Recognition", assembly: true, nectar: false, highlight: true },
      { feature: "Peer-to-Peer Recognition", assembly: true, nectar: true },
      { feature: "Manager 1:1 Tools", assembly: true, nectar: false, highlight: true },
      { feature: "Employee Surveys", assembly: true, nectar: false, highlight: true },
      { feature: "Custom Branding", assembly: true, nectar: true },
      { feature: "Slack/Teams Integration", assembly: true, nectar: true },
    ],
    analytics: [
      { feature: "Advanced Analytics Dashboard", assembly: true, nectar: false, highlight: true },
      { feature: "AI-Driven Insights", assembly: true, nectar: false, highlight: true },
      { feature: "Recognition Gap Analysis", assembly: true, nectar: false, highlight: true },
      { feature: "Performance Correlation", assembly: true, nectar: false, highlight: true },
      { feature: "Basic Participation Tracking", assembly: true, nectar: true },
      { feature: "Export Capabilities", assembly: true, nectar: true },
    ],
    support: [
      { feature: "24/7 Customer Support", assembly: true, nectar: false, highlight: true },
      { feature: "Unlimited Training", assembly: true, nectar: false, highlight: true },
      { feature: "Fast Deployment (Hours)", assembly: true, nectar: false, highlight: true },
      { feature: "GDPR Compliant", assembly: true, nectar: false, highlight: true },
      { feature: "SOC 2 Type II", assembly: true, nectar: true },
      { feature: "API Access", assembly: true, nectar: true },
    ]
  };

  const pricingData = [
    { feature: "Free Plan Available", assembly: true, nectar: false, highlight: true },
    { feature: "No Transaction Fees", assembly: true, nectar: false, highlight: true },
    { feature: "Points Never Expire", assembly: true, nectar: false, highlight: true },
    { feature: "Transparent Pricing", assembly: true, nectar: false, highlight: true },
    { feature: "Global Rewards", assembly: true, nectar: true },
    { feature: "Budget Controls", assembly: true, nectar: true },
  ];

  return (
    <CompanyLayout
      title="Assembly vs Nectar: Complete Comparison Guide | Employee Recognition Software"
      description="Compare Assembly and Nectar employee recognition platforms. See detailed analysis of features, pricing, G2 reviews, and why Assembly ranks #1 for easiest to use. Make the right choice for your team."
      ogImage="/assembly-vs-nectar-hero.png"
      canonicalUrl="https://assembly.com/compare/assembly-vs-nectar"
      keywords="Assembly vs Nectar, employee recognition software comparison, Assembly reviews, Nectar reviews, G2 rankings, employee engagement platform, recognition platform features, employee rewards software"
      author="Assembly Content Team"
    >
      {/* Hero Section - Full Width */}
      <section className="relative pt-20 pb-12 bg-gradient-to-br from-background via-primary/5 to-secondary/10 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-tr from-primary/10 via-transparent to-secondary/5"></div>
          <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-gradient-to-br from-primary/15 to-transparent rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/3 right-1/4 w-80 h-80 bg-gradient-to-bl from-secondary/12 to-transparent rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 pt-20 pb-8">
          {/* H1 Header - Full Width Above Everything */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="hidden lg:block max-w-7xl mx-auto mb-12"
          >
            <h1 className="w-full text-4xl xl:text-5xl font-bold leading-tight bg-gradient-to-r from-foreground via-foreground to-foreground/80 bg-clip-text">
              Assembly vs Nectar
            </h1>
          </motion.div>

          {/* Desktop Layout: Text Left, Image Right */}
          <div className="hidden lg:grid lg:grid-cols-2 gap-16 items-center max-w-7xl mx-auto">
            {/* Left Side - Text Stack */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-8"
            >
              <div className="space-y-6">
                <h2 className="text-2xl xl:text-3xl font-medium text-muted-foreground leading-relaxed">
                  Side-by-Side Comparison Backed by Real User Reviews
                </h2>
                
                <p className="text-lg text-muted-foreground leading-relaxed max-w-xl">
                  Choosing the right employee recognition software is about real results, not just features. Research shows <a href="https://www.quantumworkplace.com/future-of-work/importance-of-employee-recognition" target="_blank" rel="nofollow noopener" className="text-foreground/70 hover:text-foreground hover:underline">recognition programs</a> reduce turnover by 31% and make companies 12× more likely to succeed.
                </p>
              </div>
              
              <div>
                <Button 
                  size="lg" 
                  className="px-8 py-4 text-lg font-semibold bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg hover:shadow-xl transition-all duration-200"
                  onClick={() => setIsSupademoModalOpen(true)}
                >
                  See Assembly in Action
                </Button>
              </div>
            </motion.div>
            
            {/* Right Side - Placeholder Image Block */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative group"
            >
              <div className="relative">
                {/* Optional glow effect on hover */}
                <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-secondary/20 rounded-lg blur-xl transform rotate-1 group-hover:from-primary/30 group-hover:to-secondary/30 transition-all duration-300"></div>
                <img 
                  src="/lovable-uploads/assembly-vs-nectar-hero.png" 
                  alt="Assembly vs Nectar comparison - Assembly card vs Nectar card with lightning badge showing Assembly's advantages"
                  className="relative w-full h-auto rounded-xl shadow-2xl group-hover:shadow-3xl transition-shadow duration-300"
                />
              </div>
            </motion.div>
          </div>
          
          {/* Mobile Layout: H1 → Subheading → Image → Intro → CTA → Trust Badge */}
          <div className="lg:hidden space-y-8 max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center space-y-6"
            >
              <h2 className="text-3xl sm:text-4xl font-bold leading-tight bg-gradient-to-r from-foreground via-foreground to-foreground/80 bg-clip-text">
                Assembly vs Nectar
              </h2>
              
              <h2 className="text-xl sm:text-2xl font-medium text-muted-foreground leading-relaxed px-4">
                Side-by-Side Comparison Backed by Real User Reviews
              </h2>
            </motion.div>
            
            {/* Placeholder Image Block */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-secondary/20 rounded-lg blur-xl transform rotate-1"></div>
                <img 
                  src="/lovable-uploads/assembly-vs-nectar-hero.png" 
                  alt="Assembly vs Nectar comparison - Assembly card vs Nectar card with lightning badge showing Assembly's advantages"
                  className="relative w-full h-auto rounded-xl shadow-2xl"
                />
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-center space-y-6 px-4"
            >
              <p className="text-base text-muted-foreground leading-relaxed">
                Choosing the right employee recognition software is about real results, not just features. Research shows <a href="https://www.quantumworkplace.com/future-of-work/importance-of-employee-recognition" target="_blank" rel="nofollow noopener" className="text-foreground/70 hover:text-foreground hover:underline">recognition programs</a> reduce turnover by 31% and make companies 12× more likely to succeed.
              </p>
              
              <Button 
                size="lg" 
                className="w-full sm:w-auto px-8 py-4 text-lg font-semibold bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg hover:shadow-xl transition-all duration-200"
                onClick={() => setIsSupademoModalOpen(true)}
              >
                See Assembly in Action
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Content Layout with Sidebar */}
      <div className="flex min-h-screen bg-background">
        {/* Desktop Sidebar */}
        <aside className="hidden lg:block w-80 p-6">
          <div className="sticky top-20 space-y-6">
            {/* Table of Contents - Containerized */}
            <Card className="bg-card/50 backdrop-blur-sm border border-border/50 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-1 h-6 bg-gradient-to-b from-primary to-primary/50 rounded-full"></div>
                  <h3 className="text-lg font-semibold text-foreground">Contents</h3>
                </div>
                <nav className="space-y-1">
                  {tocItems.map((item, index) => (
                    <button
                      key={item.id}
                      onClick={() => scrollToSection(item.id)}
                      className={`group flex items-center w-full text-left px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                        activeSection === item.id
                          ? 'bg-primary/10 text-primary font-medium border border-primary/20'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                      }`}
                    >
                      <span className={`flex items-center justify-center w-5 h-5 rounded-md mr-3 text-xs font-medium transition-colors ${
                        activeSection === item.id 
                          ? 'bg-primary/20 text-primary' 
                          : 'bg-muted/40 text-muted-foreground group-hover:bg-muted group-hover:text-foreground'
                      }`}>
                        {index + 1}
                      </span>
                      <span>{item.label}</span>
                      {activeSection === item.id && (
                        <ChevronRight className="h-3 w-3 ml-auto text-primary" />
                      )}
                    </button>
                  ))}
                </nav>
              </CardContent>
            </Card>

            {/* Assembly Widget - Modernized */}
            <Card className="bg-gradient-to-br from-primary/5 via-primary/3 to-transparent border border-primary/20 shadow-xl">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 flex-shrink-0">
                    <img 
                      src="/assets/assembly-logo-small.png" 
                      alt="Assembly Logo"
                      className="w-12 h-12 object-contain"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1 mb-1">
                      <div className="flex">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="h-3 w-3 text-yellow-400 fill-current" />
                        ))}
                      </div>
                      <span className="text-xs font-medium text-primary ml-1">4.9/5</span>
                    </div>
                    <p className="text-xs text-muted-foreground">Top-rated platform</p>
                  </div>
                </div>
                
                <div className="space-y-2 mb-4">
                  {[
                    { text: "Free Forever Plan", icon: Check },
                    { text: "24/7 Support", icon: Check }
                  ].map((item, index) => (
                    <div key={index} className="flex items-center gap-2 text-xs">
                      <item.icon className="h-3 w-3 text-green-600 flex-shrink-0" />
                      <span className="text-muted-foreground">{item.text}</span>
                    </div>
                  ))}
                </div>

                <Button 
                  className="w-full h-9 text-sm font-medium bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 shadow-md hover:shadow-lg transition-all duration-200"
                  asChild
                >
                  <a href="/pricing">
                    Start Free
                  </a>
                </Button>
              </CardContent>
            </Card>
          </div>
        </aside>

        {/* Mobile TOC Toggle */}
        <div className="lg:hidden fixed top-4 left-4 z-40">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="bg-background/80 backdrop-blur-sm"
          >
            <Menu className="h-4 w-4" />
          </Button>
        </div>

        {/* Mobile TOC Overlay */}
        {isMobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 z-50 bg-background/80 backdrop-blur-sm">
            <div className="bg-background border-r border-border w-80 h-full p-6 overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold">Contents</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              
              <nav>
                <ul className="space-y-2">
                  {tocItems.map((item) => (
                    <li key={item.id}>
                      <button
                        onClick={() => scrollToSection(item.id)}
                        className={`flex items-center w-full text-left px-3 py-2 rounded-lg transition-colors ${
                          activeSection === item.id
                            ? 'bg-primary text-primary-foreground font-medium'
                            : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                        }`}
                      >
                        {item.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>
          </div>
        )}

        {/* Main Content */}
        <main className="flex-1 w-full bg-gradient-to-br from-background via-background/98 to-background/95">
          {/* Content Sections */}
          <div className="container mx-auto px-6 sm:px-8 lg:px-12 lg:pl-8">
            
            {/* Overview Section */}
            <section id="overview" className="py-20">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-4xl mb-8 text-left">1. Platform Comparison at a Glance</h2>
                
                <div className="max-w-6xl xl:max-w-7xl 2xl:max-w-full mx-auto mb-12">
                  <Card className="overflow-hidden shadow-xl border-0">
                    {/* Desktop Header */}
                    <div className="hidden md:block bg-gradient-to-r from-primary/5 to-secondary/5 p-6">
                      <div className="grid grid-cols-[200px_1fr_1fr] xl:grid-cols-[280px_1fr_1fr] gap-4 xl:gap-6 font-semibold text-lg">
                        <div className="text-left">Feature</div>
                        <div className="text-center">Assembly</div>
                        <div className="text-center">Nectar</div>
                      </div>
                    </div>
                    
                    {/* Mobile Header */}
                    <div className="md:hidden bg-gradient-to-r from-primary/5 to-secondary/5 p-4">
                      <h3 className="font-semibold text-lg text-center">Platform Comparison</h3>
                    </div>
                    
                    <div className="divide-y divide-border">
                      {[
                        {
                          feature: "Best For",
                          assembly: "Companies that want recognition, rewards, and culture tools in one platform, built to scale",
                          nectar: "Teams that want simple peer-to-peer recognition with basic point systems",
                          highlight: true
                        },
                        {
                          feature: "Recognition",
                          assembly: "Peer-to-peer, top-down, automated workflows, milestones, and custom programs",
                          nectar: "Peer-to-peer recognition with points and limited customization",
                          highlight: false
                        },
                        {
                          feature: "Rewards",
                          assembly: "Global catalog, gift cards, company swag, custom rewards, never-expiring points",
                          nectar: "Gift cards and digital rewards with limited global options",
                          highlight: true
                        },
                        {
                          feature: "Integrations",
                          assembly: "Slack, Microsoft Teams, HRIS (ADP, BambooHR, Rippling, more)",
                          nectar: "Slack, Microsoft Teams, basic HRIS integrations",
                          highlight: false
                        },
                        {
                          feature: "Analytics",
                          assembly: "Advanced dashboards, ROI reporting, manager insights, AI-driven analytics",
                          nectar: "Basic activity reports and participation tracking",
                          highlight: true
                        },
                        {
                          feature: "Pricing",
                          assembly: "Transparent pricing starting free, no transaction fees",
                          nectar: "Must contact sales for pricing, no public pricing available",
                          highlight: true
                        },
                        {
                          feature: "User Feedback (G2)",
                          assembly: "Highly rated for ease of use (4.9/5), customer support, and comprehensive features",
                          nectar: "Rated for basic recognition (4.7/5), but noted limitations in features and support",
                          highlight: false
                        },
                        {
                          feature: "Overall Value",
                          assembly: "Built to grow with your organization and deliver measurable ROI",
                          nectar: "Simple recognition tool, less customizable for growing organizations",
                          highlight: true
                        }
                      ].map((row, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0 }}
                          whileInView={{ opacity: 1 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.3, delay: index * 0.05 }}
                          className={`hover:bg-muted/50 transition-colors ${
                            row.highlight ? 'bg-primary/5' : ''
                          }`}
                        >
                          {/* Desktop Layout */}
                          <div className="hidden md:grid md:grid-cols-[200px_1fr_1fr] xl:grid-cols-[280px_1fr_1fr] gap-4 xl:gap-6 p-6 items-start">
                            <div className="font-semibold text-foreground flex items-center">
                              {row.feature}
                            </div>
                            <div className="text-sm text-foreground leading-relaxed">
                              {row.assembly}
                            </div>
                            <div className="text-sm text-muted-foreground leading-relaxed">
                              {row.nectar}
                            </div>
                          </div>
                          
                          {/* Mobile Layout */}
                          <div className="md:hidden p-4 space-y-4">
                            <div className="font-semibold text-foreground text-base mb-3">
                              {row.feature}
                            </div>
                            <div className="space-y-3">
                              <div className="bg-primary/10 p-3 rounded-lg">
                                <div className="font-medium text-primary text-sm mb-1">Assembly</div>
                                <div className="text-sm text-foreground leading-relaxed">
                                  {row.assembly}
                                </div>
                              </div>
                              <div className="bg-muted/50 p-3 rounded-lg">
                                <div className="font-medium text-muted-foreground text-sm mb-1">Nectar</div>
                                <div className="text-sm text-muted-foreground leading-relaxed">
                                  {row.nectar}
                                </div>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </Card>
                </div>
              </motion.div>
            </section>

            {/* Features Comparison Section */}
            <section id="features" className="py-20 bg-muted/30 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="text-left mb-12"
              >
                <h2 className="text-4xl font-bold mb-4">2. Feature Comparison</h2>
                <p className="text-base text-muted-foreground text-left mx-auto">
                  Choosing between the platforms comes down to the features your team values most. Understanding <a href="https://www.shrm.org/topics-tools/flagships/all-things-work/employee-recognition-can-go-long-way-but-has-to-be-done-right" target="_blank" rel="nofollow noopener" className="text-foreground/70 hover:text-foreground hover:underline">why recognition matters</a> helps inform the right choice. Here's a detailed look at how the two platforms compare.
                </p>
              </motion.div>

              {/* Category Tabs */}
              <div className="flex flex-wrap justify-center gap-2 mb-12">
                {Object.entries({
                  recognition: "Recognition & Engagement",
                  rewards: "Rewards & Gifting", 
                  analytics: "Analytics & Tools",
                  security: "Security & Compliance",
                  support: "Support & Satisfaction"
                }).map(([key, label]) => (
                  <button
                    key={key}
                    onClick={() => setActiveTab(key)}
                    className={`px-6 py-3 rounded-full font-medium transition-all duration-200 ${
                      activeTab === key
                        ? 'bg-primary text-primary-foreground shadow-lg'
                        : 'bg-card text-muted-foreground hover:bg-muted border'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {/* Comparison Table */}
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="max-w-6xl xl:max-w-7xl 2xl:max-w-full mx-auto"
              >
                <Card className="overflow-hidden shadow-xl border-0">
                  {/* Desktop Header */}
                  <div className="hidden md:block bg-gradient-to-r from-primary/5 to-secondary/5 p-6">
                    <div className="grid grid-cols-[200px_1fr_1fr] xl:grid-cols-[280px_1fr_1fr] gap-4 xl:gap-6 font-semibold text-lg">
                      <div>Feature</div>
                      <div className="text-center">Assembly</div>
                      <div className="text-center">Nectar</div>
                    </div>
                  </div>
                  
                  {/* Mobile Header */}
                  <div className="md:hidden bg-gradient-to-r from-primary/5 to-secondary/5 p-4">
                    <h3 className="font-semibold text-lg text-center">Feature Comparison</h3>
                  </div>
                  
                  <div className="divide-y divide-border">
                    {(() => {
                      const featureData = {
                        recognition: [
                          { feature: "Peer-to-Peer Recognition", assembly: "✅ Values-based, live social feed", nectar: "✅ Points-based recognition", highlight: false },
                          { feature: "Manager-to-Employee Recognition", assembly: "✅ Structured awards & challenges", nectar: "⚠️ Limited management tools", highlight: true },
                          { feature: "AI-Powered Recognition", assembly: "✅ Drafts text in seconds, leverages the power of small wins", nectar: "❌ Not available", highlight: true },
                          { feature: "Automated Celebrations", assembly: "✅ Birthdays, anniversaries, new hires (customizable bots)", nectar: "⚠️ Basic milestone tracking", highlight: false },
                          { feature: "Broader Engagement Tools", assembly: "✅ Announcements and engagement tools", nectar: "❌ Recognition-focused only", highlight: true }
                        ],
                        rewards: [
                          { feature: "Rewards Catalog", assembly: "✅ Gift cards, experiences, swag, culture-driven perks", nectar: "⚠️ Limited catalog, mainly gift cards", highlight: false },
                          { feature: "Points Expiration", assembly: "❌ Never expire", nectar: "⚠️ Points may expire based on company policy", highlight: true },
                          { feature: "Transaction Fees", assembly: "❌ No hidden fees", nectar: "⚠️ Platform fees may apply", highlight: true },
                          { feature: "Global Availability", assembly: "✅ Worldwide rewards access", nectar: "⚠️ Limited international options", highlight: true },
                          { feature: "Custom Rewards", assembly: "✅ Company swag and custom experiences", nectar: "⚠️ Limited customization options", highlight: true }
                        ],
                        analytics: [
                          { feature: "Engagement Analytics", assembly: "✅ Recognition + surveys, adoption, ROI reporting", nectar: "⚠️ Basic recognition activity", highlight: true },
                          { feature: "Manager Dashboards", assembly: "✅ Recognition habits, 1:1 tracking, performance context", nectar: "⚠️ Limited manager insights", highlight: false },
                          { feature: "AI Insights", assembly: "✅ Dora Hub with predictive insights", nectar: "❌ Not available", highlight: true }
                        ],
                        security: [
                          { feature: "SOC 2 Type II", assembly: "✅", nectar: "⚠️ Not clearly documented", highlight: false },
                          { feature: "GDPR Compliance", assembly: "✅", nectar: "⚠️ Limited information available", highlight: true },
                          { feature: "Enterprise Security Reviews", assembly: "✅ Fast turnaround, dedicated support", nectar: "⚠️ Less emphasis on enterprise security", highlight: true }
                        ],
                        support: [
                          { feature: "G2 Rating", assembly: "⭐ 4.9/5 (2,400+ reviews)", nectar: "⭐ 4.7/5 (Limited reviews)", highlight: true },
                          { feature: "Awards", assembly: "G2 Leader, Capterra Shortlist, Best Software 2025", nectar: "Limited industry recognition", highlight: true },
                          { feature: "Support", assembly: "✅ 24/7 support, unlimited training, rapid deployment", nectar: "⚠️ Business hours support, limited training", highlight: true },
                          { feature: "Highlighted Feedback", assembly: "\"Game changer for culture & engagement\" – HR Generalist", nectar: "\"Basic but functional\" – common sentiment", highlight: false }
                        ]
                      };
                      
                      return featureData[activeTab]?.map((row, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.3, delay: index * 0.05 }}
                          className={`hover:bg-muted/50 transition-colors ${
                            row.highlight ? 'bg-primary/5' : ''
                          }`}
                        >
                          {/* Desktop Layout */}
                          <div className="hidden md:grid md:grid-cols-[200px_1fr_1fr] xl:grid-cols-[280px_1fr_1fr] gap-4 xl:gap-6 p-6 items-start">
                            <div className="font-medium flex items-center">
                              {row.feature}
                            </div>
                            <div className="text-sm leading-relaxed">
                              {row.assembly}
                            </div>
                            <div className="text-sm text-muted-foreground leading-relaxed">
                              {row.nectar}
                            </div>
                          </div>
                          
                          {/* Mobile Layout */}
                          <div className="md:hidden p-4 space-y-4">
                            <div className="font-medium text-foreground text-base mb-3">
                              {row.feature}
                            </div>
                            <div className="space-y-3">
                              <div className="bg-primary/10 p-3 rounded-lg">
                                <div className="font-medium text-primary text-sm mb-1">Assembly</div>
                                <div className="text-sm text-foreground leading-relaxed">
                                  {row.assembly}
                                </div>
                              </div>
                              <div className="bg-muted/50 p-3 rounded-lg">
                                <div className="font-medium text-muted-foreground text-sm mb-1">Nectar</div>
                                <div className="text-sm text-muted-foreground leading-relaxed">
                                  {row.nectar}
                                </div>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      )) || [];
                    })()}
                  </div>
                </Card>
                
                {/* Closing Summary */}
                <div className="mt-12 max-w-6xl mx-auto">
                  <Card className="p-8 bg-gradient-to-r from-primary/5 to-secondary/5 border-primary/20">
                    <div className="text-left">
                      <h3 className="text-2xl font-bold mb-4 text-left">The Bottom Line</h3>
                      <p className="text-base text-muted-foreground leading-relaxed text-left">
                        While Nectar offers basic <a href="https://www.shrm.org/topics-tools/news/technology/put-recognition-employees-hands" target="_blank" rel="nofollow noopener" className="text-foreground/70 hover:text-foreground hover:underline">peer-to-peer recognition</a>, Assembly provides comprehensive tools, AI-powered insights, and stronger ROI tracking—making it the superior choice for growing teams that need more than basic point systems.
                      </p>
                    </div>
                  </Card>
                </div>
              </motion.div>
            </section>

            {/* Pricing Section */}
            <section id="pricing" className="py-20">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="text-left mb-12"
              >
                <h2 className="text-4xl font-bold mb-4">3. Pricing Models</h2>
                <p className="text-base text-muted-foreground text-left mx-auto">
                  Transparent pricing vs contact-for-pricing - see why Assembly delivers better value with no hidden costs
                </p>
              </motion.div>

              <div className="max-w-6xl xl:max-w-7xl 2xl:max-w-full mx-auto">
                <Card className="overflow-hidden shadow-xl border-0">
                  {/* Desktop Header */}
                  <div className="hidden md:block bg-gradient-to-r from-primary/5 to-secondary/5 p-6">
                    <div className="grid grid-cols-[200px_1fr_1fr] xl:grid-cols-[280px_1fr_1fr] gap-4 xl:gap-6 font-semibold text-lg">
                      <div className="text-left">Plan Level</div>
                      <div className="text-center">Assembly</div>
                      <div className="text-center">Nectar</div>
                    </div>
                  </div>
                  
                  {/* Mobile Header */}
                  <div className="md:hidden bg-gradient-to-r from-primary/5 to-secondary/5 p-4">
                    <h3 className="font-semibold text-lg text-center">Pricing Comparison</h3>
                  </div>
                  
                  <div className="divide-y divide-border">
                    {[
                      {
                        plan: "Free/Entry Plan",
                        assembly: "Free forever for up to 20 employees (Recognition) + Free forever Gifting",
                        nectar: "No free plan available - must contact sales",
                        highlight: true
                      },
                      {
                        plan: "Mid-Tier Plan",
                        assembly: "Celebrate: $2/employee/month + rewards at cost",
                        nectar: "Plus Plan: Contact sales for pricing",
                        highlight: false
                      },
                      {
                        plan: "Premium Plan",
                        assembly: "Empower: $3/employee/month + rewards at cost",
                        nectar: "Premium Plan: Contact sales for pricing",
                        highlight: false
                      },
                      {
                        plan: "Enterprise",
                        assembly: "Custom enterprise solutions with transparent pricing",
                        nectar: "Custom Plan: Contact sales for pricing",
                        highlight: false
                      },
                      {
                        plan: "Transaction Fees",
                        assembly: "❌ No transaction fees - 100% of budget goes to employees",
                        nectar: "⚠️ Platform fees may apply",
                        highlight: true
                      },
                      {
                        plan: "Point Expiration",
                        assembly: "❌ Points never expire",
                        nectar: "⚠️ May vary by plan - contact sales",
                        highlight: true
                      }
                    ].map((row, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                        className={`hover:bg-muted/50 transition-colors ${
                          row.highlight ? 'bg-primary/5' : ''
                        }`}
                      >
                        {/* Desktop Layout */}
                        <div className="hidden md:grid md:grid-cols-[200px_1fr_1fr] xl:grid-cols-[280px_1fr_1fr] gap-4 xl:gap-6 p-6 items-start">
                          <div className="font-semibold text-foreground flex items-center">
                            {row.plan}
                          </div>
                          <div className="text-sm text-foreground leading-relaxed">
                            {row.assembly}
                          </div>
                          <div className="text-sm text-muted-foreground leading-relaxed">
                            {row.nectar}
                          </div>
                        </div>
                        
                        {/* Mobile Layout */}
                        <div className="md:hidden p-4 space-y-4">
                          <div className="font-semibold text-foreground text-base mb-3">
                            {row.plan}
                          </div>
                          <div className="space-y-3">
                            <div className="bg-primary/10 p-3 rounded-lg">
                              <div className="font-medium text-primary text-sm mb-1">Assembly</div>
                              <div className="text-sm text-foreground leading-relaxed">
                                {row.assembly}
                              </div>
                            </div>
                            <div className="bg-muted/50 p-3 rounded-lg">
                              <div className="font-medium text-muted-foreground text-sm mb-1">Nectar</div>
                              <div className="text-sm text-muted-foreground leading-relaxed">
                                {row.nectar}
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </Card>
                
                {/* Pricing Summary */}
                <div className="mt-12 max-w-6xl mx-auto">
                  <Card className="p-8 bg-gradient-to-r from-primary/5 to-secondary/5 border-primary/20">
                    <div className="text-left">
                      <h3 className="text-2xl font-bold mb-4 text-left">Pricing Winner: Assembly</h3>
                      <p className="text-base text-muted-foreground leading-relaxed text-left">
                        Assembly offers transparent, upfront pricing with a free forever plan and no hidden transaction fees. Nectar requires contacting sales for all pricing information, making it difficult to budget and compare true costs.
                      </p>
                    </div>
                  </Card>
                </div>
              </div>
            </section>

            {/* Ease of Use Section */}
            <section id="ease-of-use" className="py-20">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="text-left mb-12"
              >
                <h2 className="text-4xl font-bold mb-4">
                  4. Ease of Use: Assembly wins in Highest Rated and Easiest to Use
                </h2>
               <p className="text-base text-muted-foreground text-left mx-auto mb-2">
                 When comparing employee recognition software platforms, G2's independent user rankings reveal Assembly's superior user experience and implementation success. Real companies consistently rate Assembly #1 for both highest quality and easiest to use, making it the clear choice for organizations seeking seamless adoption and maximum user engagement.
               </p>
              </motion.div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-7xl mx-auto">
                {/* Highest Rated Rankings */}
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                >
                  <Card className="overflow-hidden shadow-xl border-0 h-full">
                    <div className="bg-gradient-to-r from-primary/10 to-primary/5 p-6">
                      <h3 className="text-2xl font-bold text-center mb-2 flex items-center justify-center gap-2">
                        <Award className="h-6 w-6 text-primary" />
                        Highest Rated
                      </h3>
                      <p className="text-center text-muted-foreground text-sm">
                        <a 
                          href="https://www.g2.com/categories/employee-recognition?tab=highest_rated" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          View on G2 →
                        </a>
                      </p>
                    </div>
                    <div className="p-6">
                      <div className="space-y-3">
                        {/* Assembly - #1 */}
                        <div className="relative flex items-center justify-between p-5 bg-gradient-to-r from-primary/15 to-primary/10 rounded-xl border-2 border-primary/30 shadow-lg shadow-primary/10">
                          <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-transparent rounded-xl"></div>
                          <div className="relative flex items-center space-x-3">
                            <div className="w-7 h-7 bg-primary/30 rounded-full flex items-center justify-center shadow-md">
                              <span className="text-xs font-bold text-primary">#1</span>
                            </div>
                            <div className="w-12 h-12 bg-gradient-to-br from-primary/20 to-primary/10 rounded-xl flex items-center justify-center shadow-sm">
                              <img 
                                src="/assets/assembly-logo-small.png" 
                                alt="Assembly Logo"
                                className="w-8 h-8 object-contain"
                              />
                            </div>
                            <div>
                              <div className="font-bold text-primary">Assembly</div>
                              <p className="text-xs text-muted-foreground">Employee Recognition</p>
                            </div>
                          </div>
                          <div className="relative flex items-center space-x-3">
                            <div className="text-right">
                              <div className="text-2xl font-bold text-primary">99</div>
                            </div>
                          </div>
                        </div>

                        {/* Nectar - Lower */}
                        <div className="flex items-center justify-between p-3 bg-muted/10 rounded-lg border border-muted/30">
                          <div className="flex items-center space-x-3">
                            <div className="w-6 h-6 bg-muted/30 rounded-full flex items-center justify-center">
                              <span className="text-xs font-medium text-muted-foreground">#4</span>
                            </div>
                            <div className="w-10 h-10 bg-muted/20 rounded-lg flex items-center justify-center p-2">
                              <img src="/lovable-uploads/nectar-logo.png" alt="Nectar" className="w-full h-full object-contain" />
                            </div>
                            <div>
                              <div className="font-medium text-muted-foreground">Nectar</div>
                              <p className="text-xs text-muted-foreground/70">Recognition & Rewards</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-3">
                            <div className="text-right">
                              <div className="text-lg font-medium text-muted-foreground">98</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>
                </motion.div>

                {/* Easiest to Use Rankings */}
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <Card className="overflow-hidden shadow-xl border-0 h-full">
                    <div className="bg-gradient-to-r from-primary/10 to-primary/5 p-6">
                      <h3 className="text-2xl font-bold text-center mb-2 flex items-center justify-center gap-2">
                        <Zap className="h-6 w-6 text-primary" />
                        Easiest to Use
                      </h3>
                      <p className="text-center text-muted-foreground text-sm">
                        <a 
                          href="https://www.g2.com/categories/employee-recognition?tab=easiest_to_use" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          View on G2 →
                        </a>
                      </p>
                    </div>
                    <div className="p-6">
                      <div className="space-y-3">
                        {/* Assembly - #1 */}
                        <div className="relative flex items-center justify-between p-5 bg-gradient-to-r from-primary/15 to-primary/10 rounded-xl border-2 border-primary/30 shadow-lg shadow-primary/10">
                          <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-transparent rounded-xl"></div>
                          <div className="relative flex items-center space-x-3">
                            <div className="w-7 h-7 bg-primary/30 rounded-full flex items-center justify-center shadow-md">
                              <span className="text-xs font-bold text-primary">#1</span>
                            </div>
                            <div className="w-12 h-12 bg-gradient-to-br from-primary/20 to-primary/10 rounded-xl flex items-center justify-center shadow-sm">
                              <img 
                                src="/assets/assembly-logo-small.png" 
                                alt="Assembly Logo"
                                className="w-8 h-8 object-contain"
                              />
                            </div>
                            <div>
                              <div className="font-bold text-primary">Assembly</div>
                              <p className="text-xs text-muted-foreground">Employee Recognition</p>
                            </div>
                          </div>
                          <div className="relative flex items-center space-x-3">
                            <div className="text-right">
                              <div className="text-2xl font-bold text-primary">9.5</div>
                            </div>
                          </div>
                        </div>

                        {/* Nectar - Lower */}
                        <div className="flex items-center justify-between p-3 bg-muted/10 rounded-lg border border-muted/30">
                          <div className="flex items-center space-x-3">
                            <div className="w-6 h-6 bg-muted/30 rounded-full flex items-center justify-center">
                              <span className="text-xs font-medium text-muted-foreground">#9</span>
                            </div>
                            <div className="w-10 h-10 bg-muted/20 rounded-lg flex items-center justify-center p-2">
                              <img src="/lovable-uploads/nectar-logo.png" alt="Nectar" className="w-full h-full object-contain" />
                            </div>
                            <div>
                              <div className="font-medium text-muted-foreground">Nectar</div>
                              <p className="text-xs text-muted-foreground/70">Recognition & Rewards</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-3">
                            <div className="text-right">
                              <div className="text-lg font-medium text-muted-foreground">9.4</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              </div>
            </section>

            {/* Real User Reviews Highlights */}
            <section id="reviews" className="py-20">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="text-left mb-16"
              >
                <h2 className="text-4xl font-bold mb-4">5. Real User Reviews (Backed by G2 Data)</h2>
                <p className="text-base text-muted-foreground text-left mx-auto">
                  What actual users are saying about both platforms on <a href="https://www.g2.com/products/assembly-2021-12-09/reviews" target="_blank" rel="nofollow noopener" className="text-foreground/70 hover:text-foreground hover:underline">Assembly reviews on G2</a> and Nectar user feedback - the good and the areas for improvement
                </p>
              </motion.div>

              {/* Assembly Highlights */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="mb-16"
              >
                <Card className="overflow-hidden border-2 border-primary/20 shadow-xl">
                  <div className="bg-gradient-to-r from-primary/5 to-primary/10 p-8">
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center">
                        <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center mr-6">
                          <img 
                            src="/assets/assembly-logo-small.png" 
                            alt="Assembly Logo"
                            className="w-10 h-10 object-contain"
                          />
                        </div>
                        <div>
                        <h3 className="text-3xl font-bold">Assembly Highlights</h3>
                          <p className="text-muted-foreground mt-2">What users love about Assembly</p>
                        </div>
                      </div>
                      <Badge className="bg-primary/10 text-primary border-primary/20">4.9/5 Stars</Badge>
                    </div>
                  </div>

                  <div className="p-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                      <div>
                        <div className="text-2xl font-semibold mb-6 text-green-600 flex items-center">
                          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
                            <Check className="h-5 w-5 text-green-600" />
                          </div>
                          Top User Highlights
                        </div>
                        <div className="space-y-6 text-muted-foreground">
                          <div className="border-l-4 border-green-500 pl-4">
                            <div className="font-semibold text-foreground mb-2">Comprehensive Platform</div>
                            <p>Users consistently highlight how Assembly goes beyond basic recognition to provide surveys, 1:1s, and culture tools in one platform.</p>
                          </div>
                          <div className="border-l-4 border-green-500 pl-4">
                            <div className="font-semibold text-foreground mb-2">Transparent Pricing</div>
                            <p>Organizations appreciate Assembly's clear, upfront pricing with no hidden transaction fees, unlike competitors requiring sales calls.</p>
                          </div>
                          <div className="border-l-4 border-green-500 pl-4">
                            <div className="font-semibold text-foreground mb-2">AI-Powered Efficiency</div>
                            <p>Managers report significant time savings with Assembly's AI features that make recognition faster and more meaningful.</p>
                          </div>
                        </div>
                      </div>

                      <div>
                        <div className="text-2xl font-semibold mb-6 text-orange-600 flex items-center">
                          <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center mr-3">
                            <Clock className="h-5 w-5 text-orange-600" />
                          </div>
                          Areas for Growth
                        </div>
                        <div className="space-y-6 text-muted-foreground">
                          <div className="border-l-4 border-orange-500 pl-4">
                            <div className="font-semibold text-foreground mb-2">Advanced Customization</div>
                            <p>Some users would appreciate even deeper customization options for specific industry workflows.</p>
                          </div>
                          <div className="border-l-4 border-orange-500 pl-4">
                            <div className="font-semibold text-foreground mb-2">Mobile App Features</div>
                            <p>While the mobile experience is solid, some users request additional mobile-specific features for on-the-go recognition.</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              </motion.div>

              {/* Nectar Highlights */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <Card className="overflow-hidden shadow-xl">
                  <div className="bg-gradient-to-r from-muted/50 to-muted/30 p-8">
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center">
                        <div className="w-16 h-16 bg-secondary/10 rounded-xl flex items-center justify-center mr-6 p-3">
                          <img src="/lovable-uploads/nectar-logo.png" alt="Nectar" className="w-full h-full object-contain" />
                        </div>
                        <div>
                        <h3 className="text-3xl font-bold">Nectar Highlights</h3>
                          <p className="text-muted-foreground mt-2">What users appreciate about Nectar</p>
                        </div>
                      </div>
                      <Badge variant="outline">4.7/5 Stars</Badge>
                    </div>
                  </div>

                  <div className="p-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                      <div>
                        <div className="text-2xl font-semibold mb-6 text-green-600 flex items-center">
                          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
                            <Check className="h-5 w-5 text-green-600" />
                          </div>
                          User Positives
                        </div>
                        <div className="space-y-6 text-muted-foreground">
                          <div className="border-l-4 border-green-500 pl-4">
                            <div className="font-semibold text-foreground mb-2">Simple Recognition</div>
                            <p>Users appreciate the straightforward peer-to-peer recognition system for basic team acknowledgment.</p>
                          </div>
                          <div className="border-l-4 border-green-500 pl-4">
                            <div className="font-semibold text-foreground mb-2">Basic Integration</div>
                            <p>Nectar integrates with common tools like Slack for basic recognition workflows.</p>
                          </div>
                        </div>
                      </div>

                      <div>
                        <div className="text-2xl font-semibold mb-6 text-red-600 flex items-center">
                          <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center mr-3">
                            <X className="h-5 w-5 text-red-600" />
                          </div>
                          Common Concerns
                        </div>
                        <div className="space-y-6 text-muted-foreground">
                          <div className="border-l-4 border-red-500 pl-4">
                            <div className="font-semibold text-foreground mb-2">Limited Features</div>
                            <p>Users often note the lack of advanced features like surveys, analytics, or management tools compared to comprehensive platforms.</p>
                          </div>
                          <div className="border-l-4 border-red-500 pl-4">
                            <div className="font-semibold text-foreground mb-2">Pricing Transparency</div>
                            <p>Many users express frustration with having to contact sales for pricing information instead of transparent, published rates.</p>
                          </div>
                          <div className="border-l-4 border-red-500 pl-4">
                            <div className="font-semibold text-foreground mb-2">Support Responsiveness</div>
                            <p>Some users report slower response times and less comprehensive support compared to competitors.</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              </motion.div>
            </section>

            {/* ROI Section */}
            <section id="roi" className="py-20">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="text-left mb-16"
              >
                <h2 className="text-4xl font-bold mb-4">6. ROI & Business Impact</h2>
                <p className="text-base text-muted-foreground text-left mx-auto">
                  Understanding the return on investment and measurable business outcomes from each platform
                </p>
              </motion.div>

              <div className="max-w-6xl mx-auto space-y-12">
                {/* Assembly Section */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className="mb-12"
                >
                  <Card className="overflow-hidden border-2 border-primary/20 shadow-xl">
                    <div className="bg-gradient-to-r from-primary/5 to-primary/10 p-8">
                      <div className="flex items-center">
                        <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center mr-6">
                          <img 
                            src="/assets/assembly-logo-small.png" 
                            alt="Assembly Logo"
                            className="w-10 h-10 object-contain"
                          />
                        </div>
                        <h3 className="text-3xl font-bold">Assembly ROI</h3>
                      </div>
                    </div>

                    <div className="p-8">
                      <div className="space-y-6">
                        <div className="flex items-start">
                          <div className="w-5 h-5 rounded-full bg-green-500 mr-4 mt-1 flex-shrink-0"></div>
                          <p className="text-foreground leading-relaxed">
                            <strong>31% reduction in turnover</strong> - Comprehensive recognition and engagement tools directly impact retention
                          </p>
                        </div>
                        <div className="flex items-start">
                          <div className="w-5 h-5 rounded-full bg-green-500 mr-4 mt-1 flex-shrink-0"></div>
                          <p className="text-foreground leading-relaxed">
                            <strong>75% time savings for managers</strong> - AI-powered recognition and automated workflows increase efficiency
                          </p>
                        </div>
                        <div className="flex items-start">
                          <div className="w-5 h-5 rounded-full bg-green-500 mr-4 mt-1 flex-shrink-0"></div>
                          <p className="text-foreground leading-relaxed">
                            <strong>Transparent cost structure</strong> - No hidden fees means 100% of recognition budget reaches employees
                          </p>
                        </div>
                        <div className="flex items-start">
                          <div className="w-5 h-5 rounded-full bg-green-500 mr-4 mt-1 flex-shrink-0"></div>
                          <p className="text-foreground leading-relaxed">
                            <strong>Advanced analytics & insights</strong> - Measure engagement, recognition gaps, and business impact with detailed reporting
                          </p>
                        </div>
                      </div>
                    </div>
                  </Card>
                </motion.div>

                {/* Nectar Section */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="mb-12"
                >
                  <Card className="overflow-hidden shadow-xl">
                    <div className="bg-gradient-to-r from-muted/50 to-muted/30 p-8">
                      <div className="flex items-center">
                        <div className="w-16 h-16 bg-secondary/10 rounded-xl flex items-center justify-center mr-6 p-3">
                          <img src="/lovable-uploads/nectar-logo.png" alt="Nectar" className="w-full h-full object-contain" />
                        </div>
                        <h3 className="text-3xl font-bold">Nectar ROI</h3>
                      </div>
                    </div>

                    <div className="p-8">
                      <div className="space-y-6">
                        <div className="flex items-start">
                          <div className="w-5 h-5 rounded-full border-2 border-muted-foreground/30 mr-4 mt-1 flex-shrink-0"></div>
                          <p className="text-muted-foreground leading-relaxed">
                            Limited ROI measurement capabilities - basic reporting makes it difficult to track business impact
                          </p>
                        </div>
                        <div className="flex items-start">
                          <div className="w-5 h-5 rounded-full border-2 border-muted-foreground/30 mr-4 mt-1 flex-shrink-0"></div>
                          <p className="text-muted-foreground leading-relaxed">
                            Contact-for-pricing model makes budget planning and cost comparison challenging
                          </p>
                        </div>
                        <div className="flex items-start">
                          <div className="w-5 h-5 rounded-full border-2 border-muted-foreground/30 mr-4 mt-1 flex-shrink-0"></div>
                          <p className="text-muted-foreground leading-relaxed">
                            Basic recognition features provide limited impact on broader employee engagement metrics
                          </p>
                        </div>
                      </div>
                    </div>
                  </Card>
                </motion.div>

                {/* Winner Section */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <Card className="p-8 bg-gradient-to-r from-primary/5 to-secondary/5 border-primary/20">
                    <div className="flex items-center justify-start mb-4">
                      <Check className="h-6 w-6 text-green-600 mr-3" />
                      <div className="text-2xl font-bold text-primary">Winner: Assembly</div>
                    </div>
                    <p className="text-base text-muted-foreground leading-relaxed text-left">
                      <a href="https://www.gallup.com/workplace/650174/employee-retention-depends-getting-recognition-right.aspx" target="_blank" rel="nofollow noopener" className="text-foreground/70 hover:text-foreground hover:underline">Recognition reduces turnover</a> when tied directly to measurable outcomes like engagement, retention, and ROI. Assembly's comprehensive analytics and transparent pricing provide clearer proof of impact.
                    </p>
                  </Card>
                </motion.div>
              </div>
            </section>

            {/* Which Platform Is Right for You */}
            <section id="comparison" className="py-20">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="text-left mb-16"
              >
                <h2 className="text-4xl font-bold mb-4">7. Which Platform Is Right for You?</h2>
              </motion.div>

              <div className="max-w-6xl mx-auto space-y-8">
                {/* Nectar Section */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                >
                  <Card className="p-8 border border-muted/30">
                    <div className="flex items-center mb-6">
                      <div className="w-16 h-16 bg-secondary/10 rounded-xl flex items-center justify-center mr-6 p-3">
                        <img src="/lovable-uploads/nectar-logo.png" alt="Nectar" className="w-full h-full object-contain" />
                      </div>
                      <h3 className="text-2xl font-bold">Nectar</h3>
                    </div>
                    <p className="text-base text-muted-foreground leading-relaxed">
                      <strong>Nectar</strong> might work if your organization wants very basic peer-to-peer recognition and doesn't mind contacting sales for pricing. However, it lacks the comprehensive features, transparency, and advanced capabilities most growing teams need.
                    </p>
                  </Card>
                </motion.div>

                {/* Assembly Section */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <Card className="p-8 border-2 border-primary/20 shadow-xl">
                    <div className="flex items-center mb-6">
                      <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center mr-6">
                        <img 
                          src="/assets/assembly-logo-small.png" 
                          alt="Assembly Logo"
                          className="w-10 h-10 object-contain"
                        />
                      </div>
                      <h3 className="text-2xl font-bold">Assembly</h3>
                    </div>
                    <p className="text-base text-muted-foreground leading-relaxed">
                      <strong>Assembly</strong> is the clear choice for companies that want a comprehensive recognition and engagement platform. With AI-powered recognition, <a href="https://www.shrm.org/topics-tools/news/benefits-compensation/how-flexible-rewards-programs-boost-employee-engagement" target="_blank" rel="nofollow noopener" className="text-foreground/70 hover:text-foreground hover:underline">transparent pricing</a>, advanced analytics, and tools like surveys and 1:1s, Assembly delivers greater long-term value with proven ROI.
                    </p>
                  </Card>
                </motion.div>

                {/* Recommendation */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <Card className="p-8 bg-gradient-to-r from-primary/5 to-secondary/5 border-primary/20">
                    <div className="text-left flex items-center gap-4">
                      <div className="text-3xl">👉</div>
                      <p className="text-base leading-relaxed">
                        If you need a recognition platform with transparent pricing, comprehensive features, and proven business impact, <strong className="text-primary">Assembly is the superior choice</strong>.
                      </p>
                    </div>
                  </Card>
                </motion.div>
              </div>
            </section>

            {/* Conclusion */}
            <section id="conclusion" className="py-20 bg-muted/30 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="max-w-6xl mx-auto text-left"
              >
                <h2 className="text-4xl font-bold mb-8">8. Conclusion</h2>
                <div className="prose prose-lg mx-auto text-left">
                  <p className="text-base text-muted-foreground leading-relaxed mb-8">
                    While <strong>Nectar</strong> offers basic peer-to-peer recognition, <strong>Assembly</strong> provides a comprehensive platform that combines recognition with rewards, AI-powered tools, and engagement features that deliver measurable ROI. Assembly's transparent pricing, 24/7 support, and proven track record make it the clear winner for teams that want more than basic point systems.
                  </p>
                </div>
                
                <Card className="p-8 border-primary/20">
                  <h3 className="text-2xl font-bold mb-4 text-center">Ready to see the difference?</h3>
                  <p className="text-base text-muted-foreground mb-6 text-center">
                    🎉 <strong>Start free with Assembly today</strong> and give your team a modern recognition experience that grows with your culture.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button size="lg" className="px-8 py-4 text-lg font-semibold" onClick={() => setIsSupademoModalOpen(true)}>
                      <Play className="mr-2 h-5 w-5" />
                      See Assembly in Action
                    </Button>
                    <Button variant="outline" size="lg" className="px-8 py-4 text-lg font-semibold" asChild>
                      <a href="/pricing">
                        <ArrowRight className="mr-2 h-5 w-5" />
                        Start Free
                      </a>
                    </Button>
                  </div>
                </Card>
              </motion.div>
            </section>

            {/* FAQs */}
            <section id="faqs" className="py-20">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="text-left mb-16"
              >
                <h2 className="text-4xl font-bold mb-4">9. Frequently Asked Questions</h2>
                <p className="text-base text-muted-foreground text-left mx-auto">
                  Common questions about choosing between Assembly and Nectar
                </p>
              </motion.div>

              <div className="max-w-6xl mx-auto space-y-6">
                {[
                  {
                    question: "Is Assembly better than Nectar for small businesses?",
                    answer: "Yes, Assembly is significantly better for small businesses. Assembly offers a free forever plan for up to 20 employees while Nectar has no free plan and requires contacting sales for pricing. Assembly's transparent pricing and no transaction fees make it much more accessible for small teams."
                  },
                  {
                    question: "Which platform has better G2 ratings?",
                    answer: "Assembly significantly outperforms Nectar on G2: Assembly ranks #1 in both 'Highest Rated' (99/100) and 'Easiest to Use' (9.5/10) categories, while Nectar ranks much lower in both categories. Assembly also has a higher overall rating (4.9/5 vs 4.7/5)."
                  },
                  {
                    question: "How do the pricing models compare - Assembly vs Nectar?",
                    answer: "Assembly offers transparent, upfront pricing starting with a free plan, then $2/employee/month for Celebrate and $3/employee/month for Empower. Nectar requires contacting sales for all pricing information (Plus, Premium, Custom plans), making budget planning difficult and comparison impossible."
                  },
                  {
                    question: "What are the main feature differences between Assembly and Nectar?",
                    answer: "Assembly offers AI-powered recognition, comprehensive analytics, surveys, manager 1:1 tools, and advanced integrations. Nectar focuses primarily on basic peer-to-peer recognition with limited additional features. Assembly provides a complete employee engagement platform while Nectar is a simple recognition tool."
                  },
                  {
                    question: "Which platform offers better customer support?",
                    answer: "Assembly provides 24/7 customer support with unlimited training and rapid deployment, which users consistently praise. Nectar offers business hours support with more limited training resources. Assembly's superior support is reflected in higher G2 ratings and user satisfaction scores."
                  },
                  {
                    question: "Can I get pricing information upfront for both platforms?",
                    answer: "Assembly provides complete pricing transparency on their website with clear plan tiers and costs. Nectar requires contacting sales for all pricing information, which many users find frustrating when trying to evaluate and budget for the platform."
                  },
                  {
                    question: "Which platform is better for tracking ROI and business impact?",
                    answer: "Assembly offers advanced analytics, ROI reporting, and AI-driven insights that help measure business impact including engagement, retention, and recognition effectiveness. Nectar provides only basic activity reporting, making it difficult to measure true business value and ROI."
                  },
                  {
                    question: "Can I migrate from Nectar to Assembly?",
                    answer: "Yes, Assembly provides migration support to help you transition from Nectar. The process includes data export assistance, setup guidance, and training for your team. Many companies make this switch to gain transparent pricing, advanced features, and superior customer support."
                  }
                ].map((faq, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.05 }}
                  >
                    <Card className="p-6">
                      <div className="font-semibold mb-3">{faq.question}</div>
                      <p className="text-muted-foreground leading-relaxed">{faq.answer}</p>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </section>

          </div>
        </main>
      </div>

      {/* Modals */}
      <DemoBookingModal 
        open={isDemoModalOpen}
        onOpenChange={setIsDemoModalOpen}
      />
      
      <SupademoModal 
        open={isSupademoModalOpen}
        onOpenChange={setIsSupademoModalOpen}
      />
    </CompanyLayout>
  );
};

export default AssemblyVsNectar;